/**
 * EarthTarget - High-Fidelity Blue Marble at 1.0 AU
 * UPGRADED: Higher resolution, Fresnel atmosphere, specular oceans
 * Destination for Sol Invictus energy transmission
 */

import { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { useStore } from '../../store/useStore';

interface EarthTargetProps {
  position?: [number, number, number];
  radius?: number;
}

export function EarthTarget({
  position = [50, 0, 0],  // 1.0 AU from sun at [-30,0,0]
  radius = 1.5
}: EarthTargetProps) {
  const earthRef = useRef<THREE.Mesh>(null);
  const cloudsRef = useRef<THREE.Mesh>(null);
  const cloudsRef2 = useRef<THREE.Mesh>(null);
  const fresnelRef = useRef<THREE.Mesh>(null);
  const { view, isClipMode } = useStore();

  // Create HIGH-RESOLUTION procedural Earth textures
  const { dayTexture, nightTexture, specularTexture, cloudsTexture, bumpTexture } = useMemo(() => {
    // === DAY MAP (2048x1024) ===
    const dayCanvas = document.createElement('canvas');
    dayCanvas.width = 2048;
    dayCanvas.height = 1024;
    const dayCtx = dayCanvas.getContext('2d')!;

    // Deep ocean base with gradient
    const oceanGradient = dayCtx.createLinearGradient(0, 0, 0, 1024);
    oceanGradient.addColorStop(0, '#1a3a5c');
    oceanGradient.addColorStop(0.3, '#1a4f7c');
    oceanGradient.addColorStop(0.5, '#2060a0');
    oceanGradient.addColorStop(0.7, '#1a4f7c');
    oceanGradient.addColorStop(1, '#1a3a5c');
    dayCtx.fillStyle = oceanGradient;
    dayCtx.fillRect(0, 0, 2048, 1024);

    // Add ocean texture variation
    for (let i = 0; i < 2000; i++) {
      const x = Math.random() * 2048;
      const y = Math.random() * 1024;
      dayCtx.fillStyle = `rgba(30, 80, 140, ${Math.random() * 0.3})`;
      dayCtx.beginPath();
      dayCtx.ellipse(x, y, Math.random() * 20, Math.random() * 10, Math.random() * Math.PI, 0, Math.PI * 2);
      dayCtx.fill();
    }

    // Continent colors
    const landBase = '#2d6b3a';
    const landHighlight = '#4a8b4a';
    const desert = '#c4a35a';
    const mountain = '#6b5b47';

    // Draw continents with more detail
    const drawContinent = (path: number[][], baseColor: string) => {
      if (path.length < 2) return;
      dayCtx.fillStyle = baseColor;
      dayCtx.beginPath();
      dayCtx.moveTo(path[0][0], path[0][1]);
      for (let i = 1; i < path.length; i++) {
        dayCtx.lineTo(path[i][0], path[i][1]);
      }
      dayCtx.closePath();
      dayCtx.fill();

      // Add terrain variation
      dayCtx.fillStyle = landHighlight;
      for (let i = 0; i < 50; i++) {
        const px = path[Math.floor(Math.random() * path.length)];
        const ox = px[0] + (Math.random() - 0.5) * 100;
        const oy = px[1] + (Math.random() - 0.5) * 80;
        dayCtx.beginPath();
        dayCtx.ellipse(ox, oy, Math.random() * 30, Math.random() * 20, 0, 0, Math.PI * 2);
        dayCtx.fill();
      }
    };

    // North America (more detailed shape)
    drawContinent([
      [280, 80], [350, 100], [400, 130], [420, 180], [400, 220],
      [380, 280], [340, 300], [280, 320], [220, 300], [180, 260],
      [150, 200], [160, 140], [200, 100], [280, 80]
    ], landBase);

    // South America
    drawContinent([
      [340, 380], [380, 400], [400, 500], [380, 600], [340, 700],
      [300, 720], [280, 680], [290, 580], [300, 480], [320, 400], [340, 380]
    ], landBase);

    // Europe
    drawContinent([
      [980, 120], [1040, 130], [1080, 160], [1060, 200], [1000, 220],
      [960, 200], [950, 160], [980, 120]
    ], landBase);

    // Africa
    drawContinent([
      [1000, 280], [1080, 300], [1120, 380], [1100, 500], [1040, 580],
      [980, 560], [940, 480], [920, 380], [960, 300], [1000, 280]
    ], landBase);
    // Sahara desert
    dayCtx.fillStyle = desert;
    dayCtx.beginPath();
    dayCtx.ellipse(1020, 340, 80, 40, 0, 0, Math.PI * 2);
    dayCtx.fill();

    // Asia (large landmass)
    drawContinent([
      [1100, 100], [1300, 80], [1500, 100], [1600, 150], [1650, 220],
      [1600, 300], [1500, 340], [1400, 320], [1300, 340], [1200, 300],
      [1150, 240], [1100, 180], [1100, 100]
    ], landBase);
    // Himalayas
    dayCtx.fillStyle = mountain;
    dayCtx.beginPath();
    dayCtx.ellipse(1380, 280, 100, 25, 0, 0, Math.PI * 2);
    dayCtx.fill();

    // India
    drawContinent([
      [1300, 340], [1360, 360], [1340, 460], [1300, 500], [1260, 460],
      [1260, 380], [1300, 340]
    ], landBase);

    // Southeast Asia / Indonesia
    for (let i = 0; i < 8; i++) {
      dayCtx.fillStyle = landBase;
      dayCtx.beginPath();
      dayCtx.ellipse(1500 + i * 30, 420 + Math.random() * 60, 20, 15, Math.random(), 0, Math.PI * 2);
      dayCtx.fill();
    }

    // Australia
    drawContinent([
      [1700, 480], [1800, 500], [1850, 560], [1820, 620], [1740, 640],
      [1680, 600], [1660, 540], [1700, 480]
    ], landBase);
    // Australian desert
    dayCtx.fillStyle = desert;
    dayCtx.beginPath();
    dayCtx.ellipse(1760, 560, 60, 40, 0, 0, Math.PI * 2);
    dayCtx.fill();

    // Antarctica
    dayCtx.fillStyle = '#e8e8f0';
    dayCtx.fillRect(0, 920, 2048, 104);
    // Ice texture
    for (let i = 0; i < 100; i++) {
      dayCtx.fillStyle = `rgba(255, 255, 255, ${0.3 + Math.random() * 0.4})`;
      dayCtx.beginPath();
      dayCtx.ellipse(Math.random() * 2048, 920 + Math.random() * 104, Math.random() * 50, Math.random() * 20, 0, 0, Math.PI * 2);
      dayCtx.fill();
    }

    // Arctic
    dayCtx.fillStyle = '#e0e0f0';
    dayCtx.fillRect(0, 0, 2048, 60);

    // Greenland
    dayCtx.fillStyle = '#d0d0e0';
    dayCtx.beginPath();
    dayCtx.ellipse(580, 80, 80, 60, 0, 0, Math.PI * 2);
    dayCtx.fill();

    const day = new THREE.CanvasTexture(dayCanvas);
    day.wrapS = THREE.RepeatWrapping;
    day.wrapT = THREE.ClampToEdgeWrapping;
    day.anisotropy = 16;

    // === NIGHT MAP (City Lights) ===
    const nightCanvas = document.createElement('canvas');
    nightCanvas.width = 2048;
    nightCanvas.height = 1024;
    const nightCtx = nightCanvas.getContext('2d')!;

    nightCtx.fillStyle = '#000000';
    nightCtx.fillRect(0, 0, 2048, 1024);

    // City clusters (concentrated in populated areas)
    const cityClusters = [
      { x: 300, y: 200, count: 200, spread: 80 },   // North America East
      { x: 200, y: 180, count: 100, spread: 60 },   // North America West
      { x: 350, y: 420, count: 80, spread: 40 },    // Brazil
      { x: 1000, y: 180, count: 150, spread: 50 },  // Europe
      { x: 1400, y: 250, count: 200, spread: 100 }, // Asia
      { x: 1550, y: 280, count: 150, spread: 60 },  // China/Japan
      { x: 1300, y: 380, count: 100, spread: 50 },  // India
      { x: 1760, y: 560, count: 40, spread: 30 },   // Australia
    ];

    cityClusters.forEach(cluster => {
      for (let i = 0; i < cluster.count; i++) {
        const x = cluster.x + (Math.random() - 0.5) * cluster.spread * 2;
        const y = cluster.y + (Math.random() - 0.5) * cluster.spread * 2;
        const brightness = 0.5 + Math.random() * 0.5;
        nightCtx.fillStyle = `rgba(255, ${200 + Math.random() * 55}, ${150 + Math.random() * 50}, ${brightness})`;
        nightCtx.beginPath();
        nightCtx.arc(x, y, 1 + Math.random() * 2, 0, Math.PI * 2);
        nightCtx.fill();
      }
    });

    const night = new THREE.CanvasTexture(nightCanvas);
    night.wrapS = THREE.RepeatWrapping;

    // === SPECULAR MAP (Oceans shiny, land matte) ===
    const specCanvas = document.createElement('canvas');
    specCanvas.width = 2048;
    specCanvas.height = 1024;
    const specCtx = specCanvas.getContext('2d')!;

    // Copy day map data and invert for specular (ocean = bright, land = dark)
    specCtx.drawImage(dayCanvas, 0, 0);
    const specData = specCtx.getImageData(0, 0, 2048, 1024);
    for (let i = 0; i < specData.data.length; i += 4) {
      const r = specData.data[i];
      const g = specData.data[i + 1];
      const b = specData.data[i + 2];
      // If blue-ish (ocean), make bright; if green-ish (land), make dark
      const isOcean = b > r && b > g * 0.8;
      const specValue = isOcean ? 200 : 20;
      specData.data[i] = specValue;
      specData.data[i + 1] = specValue;
      specData.data[i + 2] = specValue;
    }
    specCtx.putImageData(specData, 0, 0);

    const specular = new THREE.CanvasTexture(specCanvas);
    specular.wrapS = THREE.RepeatWrapping;

    // === CLOUD MAP (Higher detail) ===
    const cloudsCanvas = document.createElement('canvas');
    cloudsCanvas.width = 2048;
    cloudsCanvas.height = 1024;
    const cloudsCtx = cloudsCanvas.getContext('2d')!;

    cloudsCtx.fillStyle = 'rgba(0,0,0,0)';
    cloudsCtx.fillRect(0, 0, 2048, 1024);

    // Large cloud systems
    for (let i = 0; i < 100; i++) {
      const x = Math.random() * 2048;
      const y = 100 + Math.random() * 824;
      const opacity = 0.3 + Math.random() * 0.4;
      cloudsCtx.fillStyle = `rgba(255,255,255,${opacity})`;

      // Cloud cluster
      for (let j = 0; j < 5; j++) {
        const ox = x + (Math.random() - 0.5) * 100;
        const oy = y + (Math.random() - 0.5) * 40;
        const rx = 30 + Math.random() * 80;
        const ry = 15 + Math.random() * 30;
        cloudsCtx.beginPath();
        cloudsCtx.ellipse(ox, oy, rx, ry, Math.random() * Math.PI, 0, Math.PI * 2);
        cloudsCtx.fill();
      }
    }

    // Wispy clouds
    for (let i = 0; i < 300; i++) {
      const x = Math.random() * 2048;
      const y = Math.random() * 1024;
      cloudsCtx.fillStyle = `rgba(255,255,255,${0.1 + Math.random() * 0.2})`;
      cloudsCtx.beginPath();
      cloudsCtx.ellipse(x, y, 20 + Math.random() * 40, 5 + Math.random() * 15, Math.random() * Math.PI, 0, Math.PI * 2);
      cloudsCtx.fill();
    }

    const clouds = new THREE.CanvasTexture(cloudsCanvas);
    clouds.wrapS = THREE.RepeatWrapping;

    // === BUMP MAP (Terrain elevation) ===
    const bumpCanvas = document.createElement('canvas');
    bumpCanvas.width = 2048;
    bumpCanvas.height = 1024;
    const bumpCtx = bumpCanvas.getContext('2d')!;

    // Ocean level (dark)
    bumpCtx.fillStyle = '#202020';
    bumpCtx.fillRect(0, 0, 2048, 1024);

    // Copy land areas as elevated (brighter)
    bumpCtx.globalCompositeOperation = 'lighter';
    bumpCtx.drawImage(dayCanvas, 0, 0);
    bumpCtx.globalCompositeOperation = 'source-over';

    const bump = new THREE.CanvasTexture(bumpCanvas);
    bump.wrapS = THREE.RepeatWrapping;

    return {
      dayTexture: day,
      nightTexture: night,
      specularTexture: specular,
      cloudsTexture: clouds,
      bumpTexture: bump
    };
  }, []);

  useFrame((state) => {
    const delta = state.clock.getDelta();

    if (earthRef.current) {
      earthRef.current.rotation.y += delta * 0.08;
    }

    if (cloudsRef.current) {
      cloudsRef.current.rotation.y += delta * 0.10;
    }

    if (cloudsRef2.current) {
      cloudsRef2.current.rotation.y += delta * 0.06;
    }

    // Fresnel atmosphere subtle animation
    if (fresnelRef.current) {
      const material = fresnelRef.current.material as THREE.ShaderMaterial;
      if (material.uniforms) {
        material.uniforms.time.value = state.clock.elapsedTime;
      }
    }
  });

  // Fresnel atmosphere shader
  const fresnelShader = useMemo(() => ({
    uniforms: {
      time: { value: 0 },
      color1: { value: new THREE.Color(0x4488ff) },
      color2: { value: new THREE.Color(0x88ccff) },
    },
    vertexShader: `
      varying vec3 vNormal;
      varying vec3 vPosition;
      void main() {
        vNormal = normalize(normalMatrix * normal);
        vPosition = (modelViewMatrix * vec4(position, 1.0)).xyz;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      uniform float time;
      uniform vec3 color1;
      uniform vec3 color2;
      varying vec3 vNormal;
      varying vec3 vPosition;
      void main() {
        vec3 viewDir = normalize(-vPosition);
        float fresnel = pow(1.0 - max(dot(viewDir, vNormal), 0.0), 3.0);
        vec3 color = mix(color1, color2, fresnel);
        float alpha = fresnel * 0.6;
        gl_FragColor = vec4(color, alpha);
      }
    `,
  }), []);

  return (
    <group position={position}>
      {/* Enhanced lighting for Earth - SOFT, NEUTRAL colors */}
      <pointLight position={[-4, 2, 3]} color={0xffffff} intensity={2.5} distance={20} />
      <pointLight position={[3, -1, -2]} color={0xffffff} intensity={1.5} distance={15} />
      <pointLight position={[0, 3, 0]} color={0xffffff} intensity={1} distance={12} />
      {/* Fill lights - neutral white only */}
      <pointLight position={[2, 0, 3]} color={0xffffff} intensity={0.8} distance={10} />
      <pointLight position={[-2, -2, -2]} color={0xffffff} intensity={0.5} distance={10} />

      {/* Main Earth sphere - HIGH POLY with specular oceans */}
      <mesh ref={earthRef} rotation={[0, 0, 23.5 * Math.PI / 180]}>
        <sphereGeometry args={[radius, 128, 64]} />
        <meshPhysicalMaterial
          map={dayTexture}
          emissiveMap={nightTexture}
          emissive={new THREE.Color(0xffddaa)}
          emissiveIntensity={0.4}
          bumpMap={bumpTexture}
          bumpScale={0.02}
          roughnessMap={specularTexture}
          roughness={0.8}
          metalness={0.05}
          clearcoat={0.1}
          clearcoatRoughness={0.4}
        />
      </mesh>

      {/* Cloud layer 1 - Lower altitude */}
      <mesh ref={cloudsRef} scale={1.008}>
        <sphereGeometry args={[radius, 64, 32]} />
        <meshStandardMaterial
          map={cloudsTexture}
          alphaMap={cloudsTexture}
          transparent
          opacity={0.7}
          depthWrite={false}
          side={THREE.FrontSide}
        />
      </mesh>

      {/* Cloud layer 2 - Higher altitude, slightly offset */}
      <mesh ref={cloudsRef2} scale={1.015} rotation={[0.1, 0.5, 0]}>
        <sphereGeometry args={[radius, 64, 32]} />
        <meshStandardMaterial
          map={cloudsTexture}
          alphaMap={cloudsTexture}
          transparent
          opacity={0.4}
          depthWrite={false}
          side={THREE.FrontSide}
        />
      </mesh>

      {/* Fresnel atmosphere - realistic edge glow */}
      <mesh ref={fresnelRef} scale={1.025}>
        <sphereGeometry args={[radius, 64, 32]} />
        <shaderMaterial
          attach="material"
          args={[fresnelShader]}
          transparent
          side={THREE.FrontSide}
          depthWrite={false}
          blending={THREE.AdditiveBlending}
        />
      </mesh>

      {/* Inner atmosphere glow */}
      <mesh scale={1.04}>
        <sphereGeometry args={[radius, 32, 32]} />
        <meshBasicMaterial
          color={0x6699ff}
          transparent
          opacity={0.15}
          side={THREE.BackSide}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
          toneMapped={false}
        />
      </mesh>

      {/* Outer atmosphere halo - for bloom */}
      <mesh scale={1.12}>
        <sphereGeometry args={[radius, 32, 32]} />
        <meshBasicMaterial
          color={0x4488ff}
          transparent
          opacity={0.1}
          side={THREE.BackSide}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
          toneMapped={false}
        />
      </mesh>

      {/* Outermost glow ring */}
      <mesh scale={1.25}>
        <sphereGeometry args={[radius, 32, 32]} />
        <meshBasicMaterial
          color={0x2266cc}
          transparent
          opacity={0.05}
          side={THREE.BackSide}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
          toneMapped={false}
        />
      </mesh>

      {/* GROUND RECEIVER STATION - SpaceX-style Starbase */}
      <GroundReceiverStation
        earthRadius={radius}
        position={[radius * 0.85, 0.3, radius * 0.5]}  // Position on Earth surface
      />

      {/* Label - hidden during clip mode */}
      {view.showLabels && !isClipMode && (
        <sprite position={[0, radius + 1.2, 0]} scale={[3.5, 0.875, 1]}>
          <spriteMaterial
            map={createLabelTexture('Earth @ 1.0 AU', 0x3b82f6)}
            transparent
          />
        </sprite>
      )}
    </group>
  );
}

// SpaceX-style Ground Receiver Station (Starbase aesthetic)
function GroundReceiverStation({
  earthRadius,
  position
}: {
  earthRadius: number;
  position: [number, number, number];
}) {
  const stationRef = useRef<THREE.Group>(null);
  const dishRef = useRef<THREE.Mesh>(null);

  // Orient to surface normal (pointing outward from Earth center)
  const surfaceNormal = useMemo(() => {
    return new THREE.Vector3(...position).normalize();
  }, [position]);

  // Calculate rotation to align with surface
  const rotation = useMemo(() => {
    const up = new THREE.Vector3(0, 1, 0);
    const quaternion = new THREE.Quaternion().setFromUnitVectors(up, surfaceNormal);
    const euler = new THREE.Euler().setFromQuaternion(quaternion);
    return euler;
  }, [surfaceNormal]);

  // Subtle dish tracking animation
  useFrame((state) => {
    if (dishRef.current) {
      dishRef.current.rotation.x = Math.sin(state.clock.elapsedTime * 0.5) * 0.05;
    }
  });

  const scale = 0.08; // Station scale relative to Earth

  return (
    <group ref={stationRef} position={position} rotation={rotation} scale={scale}>
      {/* === MAIN RECTENNA TOWER - SpaceX Starship-inspired === */}
      <group position={[0, 0.8, 0]}>
        {/* Tower base - massive concrete foundation */}
        <mesh position={[0, 0.1, 0]}>
          <cylinderGeometry args={[0.5, 0.6, 0.2, 8]} />
          <meshStandardMaterial color={0x555555} roughness={0.9} metalness={0.1} />
        </mesh>

        {/* Main tower - sleek white SpaceX style */}
        <mesh position={[0, 1.2, 0]}>
          <cylinderGeometry args={[0.2, 0.3, 2, 16]} />
          <meshStandardMaterial
            color={0xf0f0f0}
            roughness={0.3}
            metalness={0.4}
          />
        </mesh>

        {/* Tower details - black accent bands (SpaceX style) */}
        {[0.4, 0.8, 1.2, 1.6].map((y, i) => (
          <mesh key={i} position={[0, y, 0]}>
            <cylinderGeometry args={[0.22, 0.22, 0.04, 16]} />
            <meshStandardMaterial color={0x1a1a1a} roughness={0.5} metalness={0.3} />
          </mesh>
        ))}

        {/* Primary receiving dish - large parabolic */}
        <group ref={dishRef} position={[0, 2.2, 0]}>
          <mesh rotation={[Math.PI / 2, 0, 0]}>
            <coneGeometry args={[0.8, 0.3, 32, 1, true]} />
            <meshStandardMaterial
              color={0xeeeeee}
              metalness={0.95}
              roughness={0.05}
              side={THREE.DoubleSide}
            />
          </mesh>
          {/* Dish support ring */}
          <mesh rotation={[Math.PI / 2, 0, 0]}>
            <torusGeometry args={[0.8, 0.03, 8, 32]} />
            <meshStandardMaterial color={0x333333} metalness={0.7} roughness={0.3} />
          </mesh>
          {/* Feed horn */}
          <mesh position={[0, 0.4, 0]}>
            <coneGeometry args={[0.08, 0.2, 8]} />
            <meshStandardMaterial
              color={0xdddddd}
              metalness={0.8}
              roughness={0.2}
            />
          </mesh>
          {/* Feed struts */}
          {[0, Math.PI/2, Math.PI, Math.PI*1.5].map((angle, i) => (
            <mesh
              key={i}
              position={[Math.cos(angle) * 0.4, 0.2, Math.sin(angle) * 0.4]}
              rotation={[0.5, angle, 0]}
            >
              <cylinderGeometry args={[0.015, 0.015, 0.5, 4]} />
              <meshStandardMaterial color={0x666666} metalness={0.6} roughness={0.4} />
            </mesh>
          ))}
        </group>

        {/* Receiver active glow - subtle */}
        <pointLight position={[0, 2.3, 0]} color={0x88ffcc} intensity={0.3} distance={1.5} />
      </group>

      {/* === STARSHIP ON PAD - SpaceX iconic look === */}
      <group position={[2, 0.1, 0]}>
        {/* Launch mount / orbital launch tower simplified */}
        <mesh position={[0, 1.5, 0]}>
          <boxGeometry args={[0.15, 3, 0.15]} />
          <meshStandardMaterial color={0x888888} roughness={0.6} metalness={0.5} />
        </mesh>

        {/* Tower arm */}
        <mesh position={[0.3, 2.5, 0]} rotation={[0, 0, Math.PI/2]}>
          <boxGeometry args={[0.1, 0.6, 0.1]} />
          <meshStandardMaterial color={0x666666} roughness={0.6} metalness={0.5} />
        </mesh>

        {/* Starship - simplified iconic shape */}
        <group position={[0.5, 0, 0]}>
          {/* Super Heavy booster (bottom) */}
          <mesh position={[0, 0.8, 0]}>
            <cylinderGeometry args={[0.18, 0.18, 1.4, 16]} />
            <meshStandardMaterial color={0xcccccc} roughness={0.4} metalness={0.6} />
          </mesh>
          {/* Grid fins */}
          {[0, Math.PI/2, Math.PI, Math.PI*1.5].map((angle, i) => (
            <mesh key={i} position={[Math.cos(angle) * 0.22, 1.3, Math.sin(angle) * 0.22]} rotation={[0, angle, 0]}>
              <boxGeometry args={[0.02, 0.15, 0.12]} />
              <meshStandardMaterial color={0x333333} metalness={0.8} roughness={0.2} />
            </mesh>
          ))}

          {/* Starship (top) */}
          <mesh position={[0, 2, 0]}>
            <cylinderGeometry args={[0.16, 0.18, 1, 16]} />
            <meshStandardMaterial color={0xf5f5f5} roughness={0.3} metalness={0.5} />
          </mesh>
          {/* Nose cone */}
          <mesh position={[0, 2.7, 0]}>
            <coneGeometry args={[0.16, 0.4, 16]} />
            <meshStandardMaterial color={0xeeeeee} roughness={0.3} metalness={0.5} />
          </mesh>
          {/* Flaps */}
          {[0, Math.PI].map((angle, i) => (
            <mesh key={i} position={[Math.cos(angle) * 0.2, 2.3, Math.sin(angle) * 0.2]} rotation={[0.3, angle, 0]}>
              <boxGeometry args={[0.02, 0.4, 0.15]} />
              <meshStandardMaterial color={0x1a1a1a} metalness={0.7} roughness={0.3} />
            </mesh>
          ))}
        </group>
      </group>

      {/* === RECTENNA ARRAY - Solar farm style grid === */}
      <group position={[-1.5, 0.05, 0]}>
        {/* Array of receiving panels */}
        {Array.from({ length: 4 }).map((_, row) =>
          Array.from({ length: 3 }).map((_, col) => (
            <mesh
              key={`${row}-${col}`}
              position={[col * 0.5 - 0.5, 0.15, row * 0.4 - 0.6]}
              rotation={[-0.3, 0, 0]}
            >
              <boxGeometry args={[0.4, 0.02, 0.3]} />
              <meshStandardMaterial
                color={0x1a2a4a}
                metalness={0.4}
                roughness={0.5}
                emissive={0x001122}
                emissiveIntensity={0.3}
              />
            </mesh>
          ))
        )}
        {/* Panel supports */}
        {Array.from({ length: 4 }).map((_, i) => (
          <mesh key={i} position={[0, 0.08, i * 0.4 - 0.6]}>
            <boxGeometry args={[1.6, 0.02, 0.03]} />
            <meshStandardMaterial color={0x555555} metalness={0.6} roughness={0.4} />
          </mesh>
        ))}
      </group>

      {/* === STORAGE TANKS - SpaceX propellant farm === */}
      <group position={[1, 0.1, -1.5]}>
        {/* Large horizontal tank */}
        <mesh rotation={[0, 0, Math.PI/2]} position={[0, 0.25, 0]}>
          <capsuleGeometry args={[0.2, 0.8, 8, 16]} />
          <meshStandardMaterial color={0xf0f0f0} roughness={0.4} metalness={0.5} />
        </mesh>
        {/* Tank supports */}
        {[-0.3, 0.3].map((x, i) => (
          <mesh key={i} position={[x, 0.1, 0]}>
            <boxGeometry args={[0.08, 0.2, 0.3]} />
            <meshStandardMaterial color={0x666666} roughness={0.7} metalness={0.4} />
          </mesh>
        ))}

        {/* Vertical tank */}
        <mesh position={[0.6, 0.5, 0]}>
          <capsuleGeometry args={[0.15, 0.6, 8, 16]} />
          <meshStandardMaterial color={0xeeeeee} roughness={0.4} metalness={0.5} />
        </mesh>
      </group>

      {/* === CONTROL CENTER === */}
      <group position={[-0.5, 0.1, 1.2]}>
        {/* Main building - sleek modern */}
        <mesh position={[0, 0.2, 0]}>
          <boxGeometry args={[0.8, 0.35, 0.5]} />
          <meshStandardMaterial color={0xf5f5f5} roughness={0.4} metalness={0.3} />
        </mesh>
        {/* Windows */}
        <mesh position={[0, 0.25, 0.26]}>
          <boxGeometry args={[0.6, 0.15, 0.02]} />
          <meshStandardMaterial color={0x446688} transparent opacity={0.6} metalness={0.3} roughness={0.4} />
        </mesh>
        {/* SpaceX logo area (black accent) */}
        <mesh position={[0, 0.32, 0.26]}>
          <boxGeometry args={[0.4, 0.05, 0.01]} />
          <meshStandardMaterial color={0x1a1a1a} roughness={0.3} metalness={0.5} />
        </mesh>
      </group>

      {/* === LANDING PAD === */}
      <mesh position={[0.5, 0.02, 1]} rotation={[0, 0, 0]}>
        <cylinderGeometry args={[0.5, 0.5, 0.03, 32]} />
        <meshStandardMaterial color={0x444444} roughness={0.8} metalness={0.2} />
      </mesh>
      {/* Pad markings - X pattern */}
      <mesh position={[0.5, 0.04, 1]} rotation={[Math.PI/2, 0, Math.PI/4]}>
        <planeGeometry args={[0.6, 0.08]} />
        <meshStandardMaterial color={0xccaa00} roughness={0.6} />
      </mesh>
      <mesh position={[0.5, 0.04, 1]} rotation={[Math.PI/2, 0, -Math.PI/4]}>
        <planeGeometry args={[0.6, 0.08]} />
        <meshStandardMaterial color={0xccaa00} roughness={0.6} />
      </mesh>
    </group>
  );
}

function createLabelTexture(text: string, color: number): THREE.Texture {
  const canvas = document.createElement('canvas');
  canvas.width = 384;
  canvas.height = 96;
  const ctx = canvas.getContext('2d')!;

  ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
  ctx.fillRect(0, 0, 384, 96);

  const hexColor = '#' + color.toString(16).padStart(6, '0');
  ctx.strokeStyle = hexColor;
  ctx.lineWidth = 2;
  ctx.strokeRect(2, 2, 380, 92);

  ctx.fillStyle = hexColor;
  ctx.font = 'bold 28px monospace';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, 192, 48);

  const texture = new THREE.CanvasTexture(canvas);
  texture.needsUpdate = true;
  return texture;
}
